import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileDown, Store, UserRound } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatEUR, label, errText } from "@/lib/api";

const KIND = {
  identita: "Documento d'identità", codice_fiscale: "Codice fiscale", visura: "Visura camerale",
  agreement_generated: "Accordo GoWeb (generato)", agreement_signed: "Accordo GoWeb firmato",
  integrative_generated: "Modulo integrativo (generato)", integrative_signed: "Modulo integrativo firmato",
  order_generated: "Modulo d'ordine (generato)", order_signed: "Modulo d'ordine firmato",
};
const DOC_STATUS = { approved: ["Approvato", "text-emerald-700"], rejected: ["Rifiutato", "text-rose-700"], pending: ["In attesa", "text-amber-700"] };

const DocRow = ({ d, onOpen }) => {
  const [st, cls] = DOC_STATUS[d.status] || ["", "text-slate-500"];
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-t border-[#E2DDD5] first:border-t-0" data-testid={`history-doc-${d.id}`}>
      <div className="min-w-0">
        <div className="text-sm">{KIND[d.kind] || d.kind} <span className={`text-xs ${cls}`}>{st}</span></div>
        <div className="text-xs text-slate-500 truncate">{d.original_filename || "—"} · caricato il {formatDate(d.uploaded_at)}{d.approved_at ? ` · approvato il ${formatDate(d.approved_at)}` : ""}</div>
      </div>
      <Button variant="outline" size="sm" onClick={() => onOpen(d)} data-testid={`history-download-${d.id}`}><FileDown size={14} className="mr-1" /> Scarica</Button>
    </div>
  );
};

const Practice = ({ p, onOpen }) => (
  <div className="bg-white border border-[#E2DDD5] rounded-xl p-4 space-y-3" data-testid={`history-${p.id}`}>
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-[#F2F0EB] text-[#206342] flex items-center justify-center shrink-0">{p.type === "reseller" ? <Store size={18} /> : <UserRound size={18} />}</div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{p.type === "reseller" ? "Rivenditore" : "Cliente"} · {p.title}</div>
        <div className="text-sm text-slate-500 flex flex-wrap gap-x-3">
          {p.code && <span className="font-mono">{p.code}</span>}
          {p.piva && <span>P.IVA {p.piva}</span>}
          {p.email && <span>{p.email}</span>}
          {p.referente && <span>Referente {p.referente}</span>}
          {p.product && <span>Prodotto {p.product}</span>}
          {p.reseller && <span>Rivenditore {p.reseller}</span>}
          {p.license_price != null && <span>Licenza {formatEUR(p.license_price)}</span>}
          {p.status && <span>Stato {label(p.status)}</span>}
        </div>
        <div className="text-xs text-slate-400 mt-0.5">Autorizzata il {formatDate(p.approved_at)} · {p.documents.length} allegati</div>
      </div>
    </div>
    <div className="bg-[#F9F8F6] border border-[#E2DDD5] rounded-lg px-3 py-1">
      {p.documents.map((d) => <DocRow key={d.id} d={d} onOpen={onOpen} />)}
    </div>
  </div>
);

export const ApprovalHistory = ({ api }) => {
  const [q, setQ] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["admin-approvals-history"], queryFn: async () => (await api.get("/admin/approvals/history")).data });
  const open = async (d) => {
    try {
      const r = await api.get(`/files/${d.id}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = d.original_filename || `${d.kind}.pdf`;
      a.click();
    } catch (e) { toast.error(errText(e, "Download non riuscito")); }
  };
  const s = q.trim().toLowerCase();
  const rows = s ? data.filter((p) => [p.title, p.code, p.piva, p.email, p.reseller, p.product].some((v) => (v || "").toLowerCase().includes(s))) : data;
  return (
    <div className="space-y-3" data-testid="approvals-history">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, codice, P.IVA o email" className="max-w-sm" data-testid="history-search" />
      {isLoading && <div className="text-slate-500">Caricamento…</div>}
      {!isLoading && rows.length === 0 && <div className="bg-white border border-[#E2DDD5] rounded-xl p-10 text-center text-slate-500">Nessuna pratica autorizzata.</div>}
      {rows.map((p) => <Practice key={p.id} p={p} onOpen={open} />)}
    </div>
  );
};
